import React, { useState, useRef, useEffect, useCallback } from "react";
import { VolumeIcon, VolumeXIcon } from "@/components/ui/icon";
import { cn } from "@/lib/utils";
import { IconButton } from '@/components/common/icon-button';

interface VolumeControlProps {
  /** 初始音量值 (0-100) */
  initialVolume?: number;
  /** 是否初始静音 */
  initialMuted?: boolean;
  /** 音量变化回调 */
  onVolumeChange?: (volume: number) => void;
  /** 静音状态变化回调 */
  onMutedChange?: (muted: boolean) => void;
  /** 自定义样式类名 */
  className?: string;
  /** 是否显示音量条 */
  showVolumeBar?: boolean;
}

export function AudioVolumeControl({
  initialVolume = 50,
  initialMuted = false,
  onVolumeChange,
  onMutedChange,
  className,
  showVolumeBar = true,
}: VolumeControlProps) {
  const [volume, setVolume] = useState(initialVolume);
  const [isMuted, setIsMuted] = useState(initialMuted);
  const [isDragging, setIsDragging] = useState(false);
  const volumeBarRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // 同步音量状态
  useEffect(() => {
    if (initialVolume !== volume) {
      setVolume(initialVolume);
    }
  }, [initialVolume, volume]);

  // 同步静音状态
  useEffect(() => {
    if (initialMuted !== isMuted) {
      setIsMuted(initialMuted);
    }
  }, [initialMuted, isMuted]);

  // 处理音量变化
  const handleVolumeChange = useCallback(
    (newVolume: number) => {
      const clampedVolume = Math.max(0, Math.min(100, newVolume));
      setVolume(clampedVolume);
      onVolumeChange?.(clampedVolume);

      // 如果音量大于0且当前是静音状态，自动取消静音
      if (clampedVolume > 0 && isMuted) {
        setIsMuted(false);
        onMutedChange?.(false);
      }
    },
    [isMuted, onVolumeChange, onMutedChange]
  );

  // 处理静音切换
  const handleMuteToggle = useCallback(() => {
    const newMuted = !isMuted;
    setIsMuted(newMuted);
    onMutedChange?.(newMuted);
  }, [isMuted, onMutedChange]);

  // 计算音量条位置
  const calculateVolumeFromPosition = useCallback(
    (clientX: number, clientY: number) => {
      if (!volumeBarRef.current) return volume;

      const rect = volumeBarRef.current.getBoundingClientRect();

      const percentage = ((rect.bottom - clientY) / rect.height) * 100;

      return Math.max(0, Math.min(100, percentage));
    },
    [volume]
  );

  // 鼠标拖拽处理
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setIsDragging(true);
      const newVolume = calculateVolumeFromPosition(e.clientX, e.clientY);
      handleVolumeChange(newVolume);
    },
    [calculateVolumeFromPosition, handleVolumeChange]
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging) return;
      const newVolume = calculateVolumeFromPosition(e.clientX, e.clientY);
      handleVolumeChange(newVolume);
    },
    [isDragging, calculateVolumeFromPosition, handleVolumeChange]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // 触摸拖拽处理
  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      e.preventDefault();
      setIsDragging(true);
      const touch = e.touches[0];
      const newVolume = calculateVolumeFromPosition(
        touch.clientX,
        touch.clientY
      );
      handleVolumeChange(newVolume);
    },
    [calculateVolumeFromPosition, handleVolumeChange]
  );

  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      if (!isDragging) return;
      e.preventDefault();
      const touch = e.touches[0];
      const newVolume = calculateVolumeFromPosition(
        touch.clientX,
        touch.clientY
      );
      handleVolumeChange(newVolume);
    },
    [isDragging, calculateVolumeFromPosition, handleVolumeChange]
  );

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  // 键盘控制
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      switch (e.key) {
        case "ArrowUp":
        case "ArrowRight":
          e.preventDefault();
          handleVolumeChange(volume + 5);
          break;
        case "ArrowDown":
        case "ArrowLeft":
          e.preventDefault();
          handleVolumeChange(volume - 5);
          break;
        case "Enter":
          e.preventDefault();
          handleMuteToggle();
          break;
      }
    },
    [volume, handleVolumeChange, handleMuteToggle]
  );

  // 监听全局鼠标和触摸事件
  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.addEventListener("touchmove", handleTouchMove, {
        passive: false,
      });
      document.addEventListener("touchend", handleTouchEnd);

      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
        document.removeEventListener("touchmove", handleTouchMove);
        document.removeEventListener("touchend", handleTouchEnd);
      };
    }
  }, [
    isDragging,
    handleMouseMove,
    handleMouseUp,
    handleTouchMove,
    handleTouchEnd,
  ]);

  // 监听系统音量变化（多设备兼容）
  useEffect(() => {
    // 监听键盘音量键
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if (e.code === "AudioVolumeUp") {
        e.preventDefault();
        handleVolumeChange(Math.min(100, volume + 10));
      } else if (e.code === "AudioVolumeDown") {
        e.preventDefault();
        handleVolumeChange(Math.max(0, volume - 10));
      } else if (e.code === "AudioVolumeMute") {
        e.preventDefault();
        handleMuteToggle();
      }
    };

    document.addEventListener("keydown", handleGlobalKeyDown);

    return () => {
      document.removeEventListener("keydown", handleGlobalKeyDown);
    };
  }, [volume, handleVolumeChange, handleMuteToggle]);

  const displayVolume = isMuted ? 0 : volume;

  return (
    <div
      ref={containerRef}
      className={cn(
        "relative flex items-center group/volume flex-col",
        className
      )}
    >
      {/* 音量图标按钮 */}
      <IconButton
        aria-label={isMuted ? "volume on" : "volume off"}
        onClick={handleMuteToggle}
        onKeyDown={handleKeyDown}
        icon={isMuted ? (
          <VolumeXIcon className="w-5 h-5 text-black" />
        ) : (
          <VolumeIcon className="w-5 h-5 text-black" />
        )}
      >
      </IconButton>

      {/* 音量滑块 */}
      {showVolumeBar && (
        <div
          className={cn(
            "absolute transform -top-2 -translate-y-full z-10 bg-i-normal/60 backdrop-blur-xl rounded-sm p-2",
            "opacity-0 invisible transition-all duration-300 delay-200",
            "group-hover/volume:opacity-100 group-hover/volume:visible group-hover/volume:delay-0",
            isDragging && "opacity-100 visible delay-0"
          )}
        >
          <div
            ref={volumeBarRef}
            className={cn(
              "relative bg-i-white/50 w-1 h-20 rounded-full cursor-pointer"
            )}
            onMouseDown={handleMouseDown}
            onTouchStart={handleTouchStart}
            role="slider"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={displayVolume}
            aria-label="volume"
            tabIndex={0}
            onKeyDown={handleKeyDown}
          >
            {/* 音量进度条 */}
            <div
              className="absolute bg-i-white rounded-full"
              style={{
                height: `${displayVolume}%`,
                width: "100%",
                bottom: 0,
              }}
            />

            {/* 拖拽手柄 */}
            <div
              className={cn(
                "absolute w-2 h-2 bg-i-white rounded-full shadow-lg transform -translate-x-1/2 translate-y-1/2",
                isDragging && "cursor-grab"
              )}
              style={{
                bottom: `${displayVolume}%`,
                left: "50%",
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
